<template>
  <div>
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="850"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar
            :color="$t('Settings.Printer.MachineGroupAddDialog.Toolbar.Color')"
          >
            <v-icon dark>
              {{ $t('Settings.Printer.MachineGroupAddDialog.Toolbar.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title
            v-if="!itemToEdit"
            class="pl-3"
          >
            <div class="body-3">
              {{ $t('Settings.Printer.MachineGroupAddDialog.Toolbar.Add.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Settings.Printer.MachineGroupAddDialog.Toolbar.Add.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-toolbar-title
            v-else
            class="pl-1"
          >
            <div class="body-3">
              {{ $t('Settings.Printer.MachineGroupAddDialog.Toolbar.Edit.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Settings.Printer.MachineGroupAddDialog.Toolbar.Edit.Subtitle') }}
            </div>
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-3">
          <v-form
            ref="machineGroupForm"
            v-model="machineGroupFormValid"
            @submit.prevent
          >
            <v-container
              class="pa-0"
              fluid
            >
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="editMachineGroup.Name"
                    :counter="255"
                    :hint="$t('Settings.Printer.MachineGroupAddDialog.Field.Name.Hint')"
                    :label="$t('Settings.Printer.MachineGroupAddDialog.Field.Name.Label')"
                    :prepend-icon="$t('Settings.Printer.MachineGroupAddDialog.Field.Name.Icon')"
                    :rules="validationRules.machineGroupForm.Name"
                    class="pr-2"
                    required
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="editMachineGroup.Description"
                    :counter="255"
                    :hint="$t('Settings.Printer.MachineGroupAddDialog.Field.Description.Hint')"
                    :label="$t('Settings.Printer.MachineGroupAddDialog.Field.Description.Label')"
                    :prepend-icon="$t('Settings.Printer.MachineGroupAddDialog.Field.Description.Icon')"
                    :rules="validationRules.machineGroupForm.Description"
                    class="pr-2"
                    required
                  />
                </v-col>

                <v-col cols="12">
                  <v-select
                    v-model="editMachineGroup.Guid"
                    :hint="$t('Settings.Printer.MachineGroupAddDialog.Field.Guid.Hint')"
                    :item-text="(item) => item.alias ? `${item.alias} (${item.hostname})` : item.hostname"
                    :items="appMachines"
                    :label="$t('Settings.Printer.MachineGroupAddDialog.Field.Guid.Label')"
                    :prepend-icon="$t('Settings.Printer.MachineGroupAddDialog.Field.Guid.Icon')"
                    class="pr-2"
                    deletable-chips
                    item-value="guid"
                    multiple
                    small-chips
                  >
                    <template #selection="{item, parent, selected}">
                      <v-chip
                        :value="selected"
                        label
                        small
                      >
                        <v-icon
                          left
                          small
                        >
                          mdi-desktop-classic
                        </v-icon>

                        <span class="pr-2">
                          {{ item.alias ? `${item.alias} (${item.hostname})` : item.hostname }}
                        </span>

                        <v-icon
                          small
                          @click="parent.selectItem(item)"
                        >
                          close
                        </v-icon>
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>

                <template v-if="editMachineGroup.Type === MachineGroupTypeEnum.VOIP">
                  <v-col cols="6">
                    <v-text-field
                      v-model="editMachineGroup.VoipAgent"
                      v-mask="'######'"
                      :label="$t('Settings.VoipDomain.Dialog.VoipUser.Filed.VoipAgent.Label')"
                      :rules="validationRules.machineGroupForm.VoipAgent"
                      class="pr-2"
                      clearable
                      prepend-icon="mdi-face-agent"
                      required
                    />
                  </v-col>

                  <v-col cols="6">
                    <v-text-field
                      v-model="editMachineGroup.Queue"
                      v-mask="'######'"
                      :label="$t('Settings.VoipDomain.Dialog.VoipUser.Filed.Queue.Label')"
                      :rules="validationRules.machineGroupForm.Queue"
                      class="pr-2"
                      clearable
                      prepend-icon="mdi-queue-first-in-last-out"
                      required
                    />
                  </v-col>
                </template>

                <v-col
                  class="pr-12"
                  cols="auto"
                >
                  <v-subheader class="pl-0 mt-2">
                    {{ $t('Common.Misc.Type') }}
                  </v-subheader>

                  <v-btn-toggle
                    v-model="editMachineGroup.Type"
                    :disabled="!userIsSuperAdmin"
                    color="success"
                    dense
                    mandatory
                  >
                    <v-btn
                      :disabled="!userIsSuperAdmin"
                      :value="MachineGroupTypeEnum.PRINT"
                      text
                    >
                      PRINT
                    </v-btn>

                    <v-btn
                      :disabled="!userIsSuperAdmin"
                      :value="MachineGroupTypeEnum.VOIP"
                      text
                    >
                      VOIP
                    </v-btn>
                  </v-btn-toggle>
                </v-col>

                <v-col
                  class="pr-12"
                  cols="auto"
                >
                  <v-subheader class="pl-0 mt-3">
                    {{ $t('Common.Misc.Status') }}
                  </v-subheader>
                  <v-switch
                    v-model="editMachineGroup.Status"
                    :label="editMachineGroup.Status ? $t('Common.Button.Toggle.ActiveF') : $t('Common.Button.Toggle.InactiveF')"
                    class="pa-0 ma-0"
                    color="success"
                    inset
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>

          <v-btn
            :disabled="loading"
            :loading="loading"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import MachineGroupTypeEnum from '@/api/Enums/MachineGroupTypeEnum.js'
import {clone} from '@/lib/helper/helper.js'
import MachineGroupType from '@/api/Enums/NotifyTypeEnum.js'

export default {
  name      : 'MachineGroupAddDialog',
  components: {},
  mixins    : [AppData, Auth],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },
    itemToEdit: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      loading              : false,
      machineGroupFormValid: false,
      newMachineGroup      : null,
      tmpMachineGroup      : {
        Id           : null,
        Name         : '',
        Description  : '',
        Guid         : [],
        Status       : true,
        VoipAgent    : '',
        Queue        : '',
        Type         : MachineGroupTypeEnum.PRINT,
        MachinesCount: 0
      },
      validationRules: {
        machineGroupForm: {
          Name: [
            (v) => v && v.length >= 2 && v.length <= 255 || this.$t('Settings.Printer.MachineGroupAddDialog.Field.Name.Error.Between')
          ],
          VoipAgent: [
            (v) => !!v || this.$t('Settings.VoipDomain.Dialog.VoipUser.Filed.VoipAgent.Error.Required')
          ],
          Queue: [
            (v) => !!v || this.$t('Settings.VoipDomain.Dialog.VoipUser.Filed.Queue.Error.Required')
          ]
        }
      }
    }
  },
  computed: {
    MachineGroupTypeEnum() {
      return MachineGroupTypeEnum
    },
    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    editMachineGroup: {
      get() {
        if (this.itemToEdit) {
          this.newMachineGroup = clone(this.itemToEdit)
        } else {
          this.newMachineGroup = clone(this.tmpMachineGroup)
        }
        return this.newMachineGroup
      }
    }
  },
  watch: {
    visible(newVal) {
      this.loading = false
      if (newVal) {
        this.$nextTick(() => {
          if (this.$refs.machineGroupForm) this.$refs.machineGroupForm.resetValidation()
        })
      }
    }
  },
  created() {
  },
  mounted() {
    this.$bus.$on(window.SocketCommand.Settings.Machine.Group.Save, this.onSaveResult)
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Settings.Machine.Group.Save, this.onSaveResult)
  },
  methods: {
    onSaveButtonClick() {
      if (this.$refs.machineGroupForm) this.$refs.machineGroupForm.validate()

      if (this.machineGroupFormValid) {
        this.loading = true
        
        const payload = clone(this.editMachineGroup)
        if (this.editMachineGroup.Type === MachineGroupType.PRINT) {
          payload.VoipAgent = ''
          payload.Queue = ''
        }

        window.callAS(window.SocketCommand.Settings.Machine.Group.Save, payload)
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.FieldError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onSaveResult(data) {
      if (data && data.status === 'success') {
        this.onCancelButtonClick()
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Printer.MachineGroupAddDialog.Notification.Success'),
          type: 'success',
          icon: 'check'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Settings.Printer.MachineGroupAddDialog.Notification.Update'),
          type: 'error',
          icon: 'warning'
        })
      }
      this.loading = false
    },

    onCancelButtonClick() {
      if (this.$refs.machineGroupForm) this.$refs.machineGroupForm.resetValidation()
      this.newMachineGroup = null
      this.isVisible = false
    }
  }
}
</script>

<style scoped>

</style>
